export type ImageSize = { width: number; height: number };
export type TextSizeKey = 'square' | 'square_hd' | 'portrait_4_3' | 'portrait_3_2' | 'portrait_16_9' | 'landscape_4_3' | 'landscape_3_2' | 'landscape_16_9' | 'landscape_21_9';
export type RatioSize = '1:1' | '16:9' | '9:16' | '3:4' | '4:3';

const isPixelSizeString = (val?: string): val is `${number}x${number}` => Boolean(val && /^\d+x\d+$/i.test(val));

export function parsePngSize(bytes: Uint8Array): ImageSize | undefined {
    try {
        if (bytes.length < 24) return undefined;
        const isPng = bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
        if (!isPng) return undefined;
        const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const width = dv.getUint32(16, false);
        const height = dv.getUint32(20, false);
        if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
            return { width, height };
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export function parseJpegSize(bytes: Uint8Array): ImageSize | undefined {
    try {
        if (bytes.length < 4) return undefined;
        if (!(bytes[0] === 0xff && bytes[1] === 0xd8)) return undefined; // SOI
        let offset = 2;
        while (offset + 3 < bytes.length) {
            if (bytes[offset] !== 0xff) { offset++; continue; }
            const marker = bytes[offset + 1];
            offset += 2;
            if (offset + 1 >= bytes.length) break;
            const length = (bytes[offset] << 8) + bytes[offset + 1];
            if (length < 2) return undefined;
            const isSOF =
                (marker >= 0xc0 && marker <= 0xc3) ||
                (marker >= 0xc5 && marker <= 0xc7) ||
                (marker >= 0xc9 && marker <= 0xcb) ||
                (marker >= 0xcd && marker <= 0xcf);
            if (isSOF) {
                if (offset + 7 < bytes.length) {
                    const height = (bytes[offset + 3] << 8) + bytes[offset + 4];
                    const width = (bytes[offset + 5] << 8) + bytes[offset + 6];
                    if (width > 0 && height > 0) return { width, height };
                }
                return undefined;
            }
            offset += length;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export function parseWebpSize(bytes: Uint8Array): ImageSize | undefined {
    try {
        if (bytes.length < 30) return undefined;
        const riff = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
        const webp = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        if (riff !== 'RIFF' || webp !== 'WEBP') return undefined;
        let offset = 12;
        while (offset + 8 <= bytes.length) {
            const tag = String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
            const size = bytes[offset + 4] | (bytes[offset + 5] << 8) | (bytes[offset + 6] << 16) | (bytes[offset + 7] << 24);
            const dataStart = offset + 8;
            if (tag === 'VP8X' && dataStart + 10 <= bytes.length) {
                const w = 1 + (bytes[dataStart + 4] | (bytes[dataStart + 5] << 8) | (bytes[dataStart + 6] << 16));
                const h = 1 + (bytes[dataStart + 7] | (bytes[dataStart + 8] << 8) | (bytes[dataStart + 9] << 16));
                if (w > 0 && h > 0) return { width: w, height: h };
            }
            offset = dataStart + size + (size % 2);
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export function parseImageSize(bytes: Uint8Array, mimeType?: string, fallbackSize?: string): `${number}x${number}` | undefined {
    const fromPng = mimeType === 'image/png' ? parsePngSize(bytes) : undefined;
    const fromJpeg = mimeType === 'image/jpeg' ? parseJpegSize(bytes) : undefined;
    const fromWebp = mimeType === 'image/webp' ? parseWebpSize(bytes) : undefined;
    const chosen = fromPng || fromJpeg || fromWebp;
    if (chosen) return `${chosen.width}x${chosen.height}` as `${number}x${number}`;
    return isPixelSizeString(fallbackSize) ? fallbackSize : undefined;
}

// 根据分辨率映射尺寸
// 输出类型：'text' 返回 UI 文案键；'ratio' 返回最简比例字符串（如 '16:9'）
export function mapResolutionToSize(input?: `${number}x${number}`): TextSizeKey;
export function mapResolutionToSize(input: `${number}x${number}` | undefined, output: 'text'): TextSizeKey;
export function mapResolutionToSize(input: `${number}x${number}` | undefined, output: 'ratio'): RatioSize;
export function mapResolutionToSize(
    input: `${number}x${number}` | undefined = "0x0",
    output: 'text' | 'ratio' = 'text',
) {
    // 安全解析宽高
    const [w, h] = input.split("x").map((v) => parseInt(v, 10));
    const width = Number.isFinite(w) ? w : 0;
    const height = Number.isFinite(h) ? h : 0;

    // 判断宽高是否有效, square 兜底
    if (width <= 0 || height <= 0) {
        return (output === 'ratio' ? '1:1' : 'square');
    }

    // 计算宽高比例
    const ratio = width / height;

    // 判断宽高比例
    const isRatio = (a: number, b: number) => Math.abs(ratio - a / b) < 0.05;

    // ratio 输出
    if (output === 'ratio') {
        if (isRatio(1, 1)) return '1:1';
        if (isRatio(16, 9)) return (width > height ? '16:9' : '9:16');
        if (isRatio(4, 3)) return (width > height ? '4:3' : '3:4');
        // 其它比例兜底：按横竖向选择 16:9 / 9:16
        return (width >= height ? '16:9' : '9:16');
    }

    // text 输出：转换为最接近的尺寸键，square 兜底
    if (isRatio(1, 1) && Math.max(width, height) >= 2048) return 'square_hd';
    if (isRatio(1, 1)) return 'square';
    if (isRatio(4, 3) && width > height) return 'landscape_4_3';
    if (isRatio(3, 4) && height > width) return 'portrait_4_3';
    if (isRatio(3, 2) && width > height) return 'landscape_3_2';
    if (isRatio(2, 3) && height > width) return 'portrait_3_2';
    if (isRatio(16, 9) && width > height) return 'landscape_16_9';
    if (isRatio(9, 16) && height > width) return 'portrait_16_9';
    if (isRatio(21, 9) && width > height) return 'landscape_21_9';
    return 'square';
}

/**
 * 计算最大公约数（GCD）
 */
function gcd(a: number, b: number): number {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
        const temp = b;
        b = a % b;
        a = temp;
    }
    return a;
}

/**
 * 化简比例，例如 6:4 -> 3:2
 * @param w 宽度
 * @param h 高度
 * @returns 化简后的比例字符串，格式为 "w:h"
 */
export function simplifyRatio(w: number, h: number): string {
    const divisor = gcd(w, h);
    const sw = w / divisor;
    const sh = h / divisor;
    // 约定：对等于 7:3 / 3:7 的场景优先返回更常见的行业称呼 21:9 / 9:21
    if (sw === 7 && sh === 3) return '21:9';
    if (sw === 3 && sh === 7) return '9:21';
    return `${sw}:${sh}`;
}

/**
 * 将像素尺寸字符串（如 "1920x1080"）转换为 CSS aspect-ratio（如 "1920/1080"）。
 * 当传入非法/空值时回退为提供的 fallback（默认 '16/9'）。
 * 比例会被化简，例如 6:4 -> 3:2
 */
export function pixelSizeToAspectRatio(input?: string, fallback: string = '16/9'): string {
    try {
        if (typeof input === 'string' && /^\d+x\d+$/i.test(input)) {
            const [wStr, hStr] = input.split('x');
            const w = Number(wStr);
            const h = Number(hStr);
            if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                const simplified = simplifyRatio(w, h);
                return simplified.replace(':', '/');
            }
        }
    } catch {}
    return fallback;
}

/**
 * 将像素尺寸（"WxH"）近似为常用比例（如 16:9 / 4:3 / 3:2 / 1:1 / 9:16 / 3:4 / 2:3 / 21:9）。
 * - 仅当与某个常用比例的误差 <= tolerance 才返回该比例；否则返回 undefined（交由调用方回退显示原始值）。
 */
export function pixelSizeToCommonRatio(
  input?: string,
  opts?: { tolerance?: number }
): RatioSize | '3:2' | '2:3' | '21:9' | '9:21' | undefined {
  try {
    if (typeof input !== 'string' || !/^\d+x\d+$/i.test(input)) return undefined;
    const [wStr, hStr] = input.toLowerCase().split('x');
    const w = Number(wStr);
    const h = Number(hStr);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return undefined;
    const ratio = w / h;
    const tol = Number.isFinite(opts?.tolerance) ? Number(opts?.tolerance) : 0.05;
    if (!Number.isFinite(tol) || tol < 0) return undefined;

    const landscape = w >= h;
    const candidates: Array<{ label: any; value: number }> = landscape
      ? [
          { label: '1:1', value: 1 / 1 },
          { label: '4:3', value: 4 / 3 },
          { label: '3:2', value: 3 / 2 },
          { label: '16:9', value: 16 / 9 },
          { label: '21:9', value: 21 / 9 },
        ]
      : [
          { label: '1:1', value: 1 / 1 },
          { label: '3:4', value: 3 / 4 },
          { label: '2:3', value: 2 / 3 },
          { label: '9:16', value: 9 / 16 },
          { label: '9:21', value: 9 / 21 },
        ];

    let best: { label: any; diff: number } | undefined;
    for (const c of candidates) {
      const diff = Math.abs(ratio - c.value);
      if (!best || diff < best.diff) best = { label: c.label, diff };
    }
    if (best && best.diff <= tol) return best.label;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * 解析像素尺寸字符串（"WxH"）为 {width,height}。容忍前后空格、大小写与 x 两侧空格。
 * 非法输入返回 undefined。
 */
export function parseWxH(raw?: unknown): ImageSize | undefined {
    if (typeof raw !== 'string') return undefined;
    const m = raw.trim().toLowerCase().match(/^(\d+)\s*x\s*(\d+)$/);
    if (!m) return undefined;
    const width = Number(m[1]);
    const height = Number(m[2]);
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return undefined;
    return { width, height };
}

/**
 * 归一化像素尺寸字符串为规范的 "WxH"（去空格、小写）。非法输入返回 undefined。
 */
export function normalizeSizeString(raw?: unknown): `${number}x${number}` | undefined {
    const wh = parseWxH(raw);
    return wh ? (`${wh.width}x${wh.height}` as `${number}x${number}`) : undefined;
}

/**
 * 从节点 data 解析真实媒体分辨率，统一来源优先级：
 * generated.size（生成节点）→ 顶层 size（上传素材）→ 顶层 width/height（旧数据兜底）。
 * 返回规范化的 "WxH"，无有效分辨率时返回 undefined。
 */
export function resolveMediaSizeLabel(data: unknown): `${number}x${number}` | undefined {
    const d = data as any;
    const fromGenerated = normalizeSizeString(d?.generated?.size);
    if (fromGenerated) return fromGenerated;
    const fromTop = normalizeSizeString(d?.size);
    if (fromTop) return fromTop;
    const w = Number(d?.width);
    const h = Number(d?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        return `${Math.round(w)}x${Math.round(h)}` as `${number}x${number}`;
    }
    return undefined;
}

/**
 * 预加载图片并返回自然尺寸（"{w}x{h}"）。在非浏览器环境返回 undefined。
 */
export function preloadImageAndGetSize(url: string): Promise<`${number}x${number}` | undefined> {
    if (typeof window === 'undefined') return Promise.resolve(undefined);
    return new Promise((resolve) => {
        try {
            const img = document.createElement('img');
            img.onload = () => {
                const w = img.naturalWidth || img.width;
                const h = img.naturalHeight || img.height;
                if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                    resolve(`${w}x${h}` as `${number}x${number}`);
                } else {
                    resolve(undefined);
                }
            };
            img.onerror = () => resolve(undefined);
            img.src = url;
        } catch {
            resolve(undefined);
        }
    });
}

/**
 * 从 HTMLImageElement 获取自然尺寸（"{w}x{h}"）。
 */
export function getNaturalSizeFromImgEl(img: HTMLImageElement): `${number}x${number}` | undefined {
    try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
            return `${w}x${h}` as `${number}x${number}`;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export type GridSplitPiece = {
  row: number;
  col: number;
  width: number;
  height: number;
  blob: Blob;
  type: 'image/png';
};

/**
 * 将图片 URL 按 rows×cols 网格裁切成多个 PNG（浏览器端）。
 *
 * 说明：
 * - 优先使用 fetch + createImageBitmap，避免跨域 <img> drawImage 造成 canvas taint
 * - 若无法使用 createImageBitmap，则回退为 HTMLImageElement（需要图片服务端允许 CORS）
 */
export async function splitImageUrlToPngGrid(params: {
  url: string;
  rows: number;
  cols: number;
}): Promise<{ sourceWidth: number; sourceHeight: number; pieces: GridSplitPiece[] }> {
  const { url, rows, cols } = params;
  if (typeof window === 'undefined') {
    throw new Error('splitImageUrlToPngGrid can only run in the browser');
  }
  if (!url) throw new Error('Missing image url');
  if (!Number.isFinite(rows) || rows <= 0) throw new Error('Invalid rows');
  if (!Number.isFinite(cols) || cols <= 0) throw new Error('Invalid cols');

  const toPngBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob>((resolve, reject) => {
      try {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to encode PNG'));
        }, 'image/png');
      } catch (e) {
        reject(e);
      }
    });

  const loadViaImageElement = async (src: string): Promise<{ w: number; h: number; draw: (ctx: CanvasRenderingContext2D, sx: number, sy: number, sw: number, sh: number, dw: number, dh: number) => void; dispose: () => void }> => {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = document.createElement('img');
      // Try CORS-safe draw; if server doesn't allow it, drawImage will taint canvas.
      el.crossOrigin = 'anonymous';
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = src;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    return {
      w,
      h,
      draw: (ctx, sx, sy, sw, sh, dw, dh) => {
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, dw, dh);
      },
      dispose: () => {},
    };
  };

  const loadViaFetchBitmap = async (src: string): Promise<{ w: number; h: number; draw: (ctx: CanvasRenderingContext2D, sx: number, sy: number, sw: number, sh: number, dw: number, dh: number) => void; dispose: () => void }> => {
    const res = await fetch(src, { method: 'GET' });
    if (!res.ok) throw new Error(`Failed to fetch image (${res.status})`);
    const blob = await res.blob();
    if (typeof (window as any).createImageBitmap !== 'function') {
      // Fallback to <img>
      const objectUrl = URL.createObjectURL(blob);
      const loaded = await loadViaImageElement(objectUrl);
      return {
        ...loaded,
        dispose: () => {
          try {
            URL.revokeObjectURL(objectUrl);
          } catch {}
          try {
            loaded.dispose();
          } catch {}
        },
      };
    }
    const bitmap = await createImageBitmap(blob);
    return {
      w: bitmap.width,
      h: bitmap.height,
      draw: (ctx, sx, sy, sw, sh, dw, dh) => {
        ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, dw, dh);
      },
      dispose: () => {
        try {
          bitmap.close();
        } catch {}
      },
    };
  };

  // Prefer fetch+bitmap; fallback to <img> if fetch fails (e.g., auth/CORS)
  const source = await loadViaFetchBitmap(url).catch(() => loadViaImageElement(url));
  const sourceWidth = source.w;
  const sourceHeight = source.h;
  if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight) || sourceWidth <= 0 || sourceHeight <= 0) {
    source.dispose();
    throw new Error('Invalid image dimensions');
  }

  const baseW = Math.floor(sourceWidth / cols);
  const baseH = Math.floor(sourceHeight / rows);
  if (baseW <= 0 || baseH <= 0) {
    source.dispose();
    throw new Error('Image too small to split');
  }

  const pieces: GridSplitPiece[] = [];
  try {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const sx = c * baseW;
        const sy = r * baseH;
        const sw = c === cols - 1 ? sourceWidth - baseW * (cols - 1) : baseW;
        const sh = r === rows - 1 ? sourceHeight - baseH * (rows - 1) : baseH;

        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context not available');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        source.draw(ctx, sx, sy, sw, sh, sw, sh);
        const blob = await toPngBlob(canvas);
        pieces.push({ row: r, col: c, width: sw, height: sh, blob, type: 'image/png' });
      }
    }
  } finally {
    source.dispose();
  }

  return { sourceWidth, sourceHeight, pieces };
}